import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  Loader2,
  CalendarClock,
} from "lucide-react";

const EMAILJS_SERVICE_ID = "service_27s7r8l";
const EMAILJS_TEMPLATE_ID = "template_shi790v";
const EMAILJS_PUBLIC_KEY = "ueta8-QuNh_QhyERT";

const CONTACT_ITEMS = [
  {
    icon: Mail,
    title: "Correo principal",
    value: "contacto@contadoresrp.com",
    href: "mailto:contacto@contadoresrp.com",
  },
  {
    icon: MapPin,
    title: "Dirección oficina matriz",
    value: "Av. de la Libertad 1041, Tepeyac Casino, Prados Tepeyac, 45047 Zapopan, Jalisco",
  },
  {
    icon: Clock,
    title: "Horario de atención",
    value: "Lunes a viernes, 9:00 a 18:00 hrs",
  },
];

export default function Contacto() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa los campos requeridos.");
      return;
    }
    setLoading(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone,
          company: form.company,
          message: form.message,
          to_name: "Contadores RP",
          reply_to: form.email,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      toast.success("¡Mensaje enviado! Te contactaremos a la brevedad.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("No pudimos enviar tu mensaje. Intenta de nuevo o escríbenos por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contacto-section" className="py-20 sm:py-28 relative">
      <div className="rp-container">
        <div className="rp-reveal max-w-3xl">
          <span className="rp-kicker">Contacto</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" style={{ color: "var(--rp-ink)" }}>
            Conversemos sobre la <span className="rp-gradient-text">decisión que estás postergando</span>.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600">
            Agenda un diagnóstico o envíanos un mensaje. Te responderemos dentro del horario de atención.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-8">
          {/* Form */}
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="rp-reveal rp-card p-6 sm:p-8"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Nombre *
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  data-testid="contact-input-name"
                  className="rp-input"
                  placeholder="Tu nombre completo"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Correo *
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  data-testid="contact-input-email"
                  className="rp-input"
                  placeholder="tu@correo.com"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Teléfono
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="contact-input-phone"
                  className="rp-input"
                  placeholder="+52 ..."
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Empresa
                </label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  data-testid="contact-input-company"
                  className="rp-input"
                  placeholder="Nombre de tu empresa"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                Mensaje *
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                data-testid="contact-input-message"
                className="rp-input"
                placeholder="Cuéntanos brevemente cómo podemos ayudarte..."
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500">
                Al enviar aceptas que utilicemos tus datos para responder tu solicitud.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="rp-btn rp-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Enviar mensaje
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Contact info */}
          <div className="rp-reveal space-y-3">
            {CONTACT_ITEMS.map((c, i) => {
              const Icon = c.icon;
              const Tag = c.href ? "a" : "div";
              return (
                <Tag
                  key={i}
                  href={c.href}
                  target={c.href?.startsWith("http") ? "_blank" : undefined}
                  rel={c.href?.startsWith("http") ? "noreferrer" : undefined}
                  data-testid={`contact-item-${i}`}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200 hover:border-[color:var(--rp-blue)] transition-colors group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: "rgba(71,118,185,0.08)" }}
                  >
                    <Icon className="w-5 h-5" style={{ color: "var(--rp-blue)" }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">{c.title}</div>
                    <div className="text-sm font-semibold mt-0.5 break-words" style={{ color: "var(--rp-ink)" }}>
                      {c.value}
                    </div>
                  </div>
                </Tag>
              );
            })}

            <a
              href="https://calendly.com/contadoresrp"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-calendly-cta"
              className="flex items-center justify-between p-4 rounded-xl text-white relative overflow-hidden group"
              style={{ background: "linear-gradient(135deg, var(--rp-blue), var(--rp-green))" }}
            >
              <div className="flex items-center gap-3">
                <CalendarClock className="w-5 h-5" />
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-80">Calendly</div>
                  <div className="font-semibold">Agenda tu diagnóstico</div>
                </div>
              </div>
              <span className="text-sm font-semibold opacity-90 group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </div>
        </div>

        {/* Map */}
        <div className="mt-12 rp-reveal rounded-2xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-40px_rgba(60,57,80,0.3)]">
          <iframe
            data-testid="google-map"
            title="Contadores RP — Oficina Matriz Zapopan"
            src="https://www.google.com/maps?q=Av.+de+la+Libertad+1041,+Prados+Tepeyac,+45047+Zapopan,+Jalisco&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
